"use client";

import { Camera, Eye, EyeOff, Rocket, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "@/spa/routerCompat";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GitHubMark } from "./GitHubMark";
import { fetchWithCsrf } from "./client/fetchWithCsrf";
import { resolveApiUrl } from "./client/resolveApiUrl";
import { authClient } from "./client/authClient";
import { oauthErrorUserMessage } from "./client/oauthErrorMessage";
import { signInWithPlatformPasskey } from "./client/passkeySignIn";
import { resolveSafeRedirect } from "../security/safeRedirect";

const characterFill = "#050505";
const characterBorder = "rgba(255,255,255,0.82)";
const characterFeature = "#ffffff";
const characterFeatureDark = "#050505";

type LoginPageProps = {
  callbackURL: string;
  oauth?: { error: string | null; errorDescription: string | null; loggedIn?: boolean } | undefined;
  initialMode?: AuthMode;
  resetToken?: string | null;
  resetError?: string | null;
};

type AuthMode = "signin" | "signup" | "forgot" | "reset";

type PupilProps = {
  size?: number;
  maxDistance?: number;
  pupilColor?: string;
  forceLookX?: number;
  forceLookY?: number;
};

type EyeBallProps = {
  size?: number;
  pupilSize?: number;
  maxDistance?: number;
  eyeColor?: string;
  pupilColor?: string;
  isBlinking?: boolean;
  forceLookX?: number;
  forceLookY?: number;
};

type CharacterPosition = {
  faceX: number;
  faceY: number;
  bodySkew: number;
};

const Pupil = ({
  size = 12,
  maxDistance = 5,
  pupilColor = characterFeatureDark,
  forceLookX,
  forceLookY
}: PupilProps) => {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const pupilRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMouseX(event.clientX);
      setMouseY(event.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const calculatePupilPosition = () => {
    if (!pupilRef.current) return { x: 0, y: 0 };

    if (forceLookX !== undefined && forceLookY !== undefined) {
      return { x: forceLookX, y: forceLookY };
    }

    const pupil = pupilRef.current.getBoundingClientRect();
    const pupilCenterX = pupil.left + pupil.width / 2;
    const pupilCenterY = pupil.top + pupil.height / 2;
    const deltaX = mouseX - pupilCenterX;
    const deltaY = mouseY - pupilCenterY;
    const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);
    const angle = Math.atan2(deltaY, deltaX);

    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance
    };
  };

  const pupilPosition = calculatePupilPosition();

  return (
    <div
      ref={pupilRef}
      className="rounded-full"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: pupilColor,
        transform: `translate(${pupilPosition.x}px, ${pupilPosition.y}px)`,
        transition: "transform 0.1s ease-out"
      }}
    />
  );
};

const EyeBall = ({
  size = 48,
  pupilSize = 16,
  maxDistance = 10,
  eyeColor = characterFeature,
  pupilColor = characterFeatureDark,
  isBlinking = false,
  forceLookX,
  forceLookY
}: EyeBallProps) => {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const eyeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMouseX(event.clientX);
      setMouseY(event.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const calculatePupilPosition = () => {
    if (!eyeRef.current) return { x: 0, y: 0 };

    if (forceLookX !== undefined && forceLookY !== undefined) {
      return { x: forceLookX, y: forceLookY };
    }

    const eye = eyeRef.current.getBoundingClientRect();
    const eyeCenterX = eye.left + eye.width / 2;
    const eyeCenterY = eye.top + eye.height / 2;
    const deltaX = mouseX - eyeCenterX;
    const deltaY = mouseY - eyeCenterY;
    const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);
    const angle = Math.atan2(deltaY, deltaX);

    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance
    };
  };

  const pupilPosition = calculatePupilPosition();

  return (
    <div
      ref={eyeRef}
      className="flex items-center justify-center rounded-full transition-all duration-150"
      style={{
        width: `${size}px`,
        height: isBlinking ? "2px" : `${size}px`,
        backgroundColor: eyeColor,
        overflow: "hidden"
      }}
    >
      {!isBlinking ? (
        <div
          className="rounded-full"
          style={{
            width: `${pupilSize}px`,
            height: `${pupilSize}px`,
            backgroundColor: pupilColor,
            transform: `translate(${pupilPosition.x}px, ${pupilPosition.y}px)`,
            transition: "transform 0.1s ease-out"
          }}
        />
      ) : null}
    </div>
  );
};

export const LoginPage = ({ callbackURL, oauth, initialMode = "signin", resetToken, resetError }: LoginPageProps) => {
  const { t } = useTranslation();
  const loggedIn = oauth?.loggedIn ?? false;
  const oauthMessage =
    oauth !== undefined ? oauthErrorUserMessage(oauth.error, oauth.errorDescription, t) : null;
  const showLoggedInOauthRecovery = loggedIn && oauthMessage;
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [formMessage, setFormMessage] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [formError, setFormError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
  const [isBlackBlinking, setIsBlackBlinking] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking, setIsPurplePeeking] = useState(false);
  const purpleRef = useRef<HTMLDivElement>(null);
  const blackRef = useRef<HTMLDivElement>(null);
  const yellowRef = useRef<HTMLDivElement>(null);
  const orangeRef = useRef<HTMLDivElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === "reset" && (!resetToken || resetError)) {
      setFormError(t("login.resetLinkInvalid"));
    }
  }, [mode, resetError, resetToken, t]);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview(null);
      return;
    }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const readAuthError = async (res: Response): Promise<string> => {
    const data = (await res.json().catch(() => ({}))) as {
      error?: string | { message?: string };
      message?: string;
    };
    if (typeof data.error === "string" && data.error.trim()) return data.error;
    if (typeof data.error === "object" && typeof data.error.message === "string" && data.error.message.trim()) {
      return data.error.message;
    }
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    return t(mode === "signup" ? "login.createFailed" : "login.emailSignInFailed");
  };

  const handleSignInClick = useCallback(async () => {
    const btn = document.getElementById("sign-in");
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.disabled = true;
    const errorCallbackURL = `${window.location.origin}/login${window.location.search}`;
    const res = await fetch(resolveApiUrl("/api/auth/sign-in/social"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider: "github", callbackURL, errorCallbackURL })
    });
    if (res.redirected) {
      window.location.href = res.url;
      return;
    }
    const loc = res.headers.get("Location");
    if (loc) {
      window.location.href = loc;
      return;
    }
    const data = (await res.json().catch(() => ({}))) as { url?: string };
    if (data.url) {
      window.location.href = data.url;
      return;
    }
    btn.disabled = false;
    window.alert(t("login.signInFailed"));
  }, [callbackURL, t]);

  const handlePasskeySignIn = async (): Promise<void> => {
    setFormError("");
    setFormMessage("");
    if (!("PublicKeyCredential" in window) || !window.isSecureContext) {
      setFormError(t("login.passkeyUnsupported"));
      return;
    }
    setIsLoading(true);
    try {
      const result = await signInWithPlatformPasskey();
      if (!result.ok) {
        setFormError(result.cancelled ? t("login.passkeyCancelled") : t("login.passkeyFailed"));
        return;
      }
      window.location.href = callbackURL;
    } catch {
      setFormError(t("login.passkeyFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMouseX(event.clientX);
      setMouseY(event.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    let blinkEndTimeout: ReturnType<typeof setTimeout> | undefined;

    const scheduleBlink = () => {
      const blinkTimeout = setTimeout(
        () => {
          setIsPurpleBlinking(true);
          blinkEndTimeout = setTimeout(() => {
            setIsPurpleBlinking(false);
            scheduleBlink();
          }, 150);
        },
        Math.random() * 4000 + 3000
      );

      return blinkTimeout;
    };

    const blinkTimeout = scheduleBlink();

    return () => {
      clearTimeout(blinkTimeout);
      if (blinkEndTimeout !== undefined) clearTimeout(blinkEndTimeout);
    };
  }, []);

  useEffect(() => {
    let blinkEndTimeout: ReturnType<typeof setTimeout> | undefined;

    const scheduleBlink = () => {
      const blinkTimeout = setTimeout(
        () => {
          setIsBlackBlinking(true);
          blinkEndTimeout = setTimeout(() => {
            setIsBlackBlinking(false);
            scheduleBlink();
          }, 150);
        },
        Math.random() * 4000 + 3000
      );

      return blinkTimeout;
    };

    const blinkTimeout = scheduleBlink();

    return () => {
      clearTimeout(blinkTimeout);
      if (blinkEndTimeout !== undefined) clearTimeout(blinkEndTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isTyping) {
      setIsLookingAtEachOther(false);
      return;
    }

    setIsLookingAtEachOther(true);
    const timer = setTimeout(() => {
      setIsLookingAtEachOther(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [isTyping]);

  useEffect(() => {
    if (password.length === 0 || !showPassword) {
      setIsPurplePeeking(false);
      return;
    }

    const peekTimeout = setTimeout(
      () => {
        setIsPurplePeeking(true);
        window.setTimeout(() => {
          setIsPurplePeeking(false);
        }, 800);
      },
      Math.random() * 3000 + 2000
    );

    return () => clearTimeout(peekTimeout);
  }, [password, showPassword, isPurplePeeking]);

  const calculatePosition = (ref: RefObject<HTMLDivElement | null>): CharacterPosition => {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };

    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 3;
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;

    return {
      faceX: Math.max(-15, Math.min(15, deltaX / 20)),
      faceY: Math.max(-10, Math.min(10, deltaY / 30)),
      bodySkew: Math.max(-6, Math.min(6, -deltaX / 120))
    };
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    setFormMessage("");
    setIsLoading(true);
    try {
      if (mode === "forgot") {
        const res = await fetch(resolveApiUrl("/api/auth/request-password-reset"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            redirectTo: `${window.location.origin}/login?mode=reset`
          })
        });
        if (!res.ok) {
          setFormError(await readAuthError(res));
          return;
        }
        setFormMessage(t("login.resetEmailSent"));
        return;
      }

      if (mode === "reset") {
        if (!resetToken || resetError) {
          setFormError(t("login.resetLinkInvalid"));
          return;
        }
        if (password !== confirmPassword) {
          setFormError(t("login.passwordsDoNotMatch"));
          return;
        }
        const res = await fetch(resolveApiUrl("/api/auth/reset-password"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newPassword: password, token: resetToken })
        });
        if (!res.ok) {
          setFormError(await readAuthError(res));
          return;
        }
        setPassword("");
        setConfirmPassword("");
        setMode("signin");
        setFormMessage(t("login.passwordResetComplete"));
        window.history.replaceState({}, "", "/login");
        return;
      }

      const endpoint = mode === "signup" ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email";
      const body =
        mode === "signup"
          ? { name: name.trim() || email.trim(), email: email.trim(), password, callbackURL }
          : { email: email.trim(), password, rememberMe, callbackURL };
      const res = await fetch(resolveApiUrl(endpoint), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        setFormError(await readAuthError(res));
        return;
      }
      if (mode === "signup" && avatarFile) {
        const upload = await fetchWithCsrf("/api/avatar", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": avatarFile.type || "application/octet-stream" },
          body: avatarFile
        });
        if (!upload.ok) {
          setFormError(t("login.avatarSaveFailed"));
          return;
        }
      }
      window.location.href = callbackURL;
    } catch {
      setFormError(t("login.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  const purplePos = calculatePosition(purpleRef);
  const blackPos = calculatePosition(blackRef);
  const yellowPos = calculatePosition(yellowRef);
  const orangePos = calculatePosition(orangeRef);
  const isPasswordVisibleWithValue = password.length > 0 && showPassword;
  const isPasswordHiddenWithValue = password.length > 0 && !showPassword;
  const modeTitle = t(`login.mode.${mode}.title`);
  const modeSubtitle = t(`login.mode.${mode}.subtitle`);
  const submitLabel = isLoading ? t(`login.mode.${mode}.pending`) : t(`login.mode.${mode}.submit`);
  const skipTarget = showLoggedInOauthRecovery
    ? "#continue-dashboard"
    : mode === "signin" || mode === "signup"
      ? "#sign-in"
      : "#auth-form";

  return (
    <div className="min-h-svh bg-background text-foreground font-sans">
      <a
        href={skipTarget}
        className="bg-background text-foreground sr-only z-50 rounded-md px-3 py-2 focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        {showLoggedInOauthRecovery ? t("login.skipContinue") : t("login.skipSignIn")}
      </a>
      <div className="grid min-h-svh lg:grid-cols-2">
        <div className="relative hidden flex-col justify-between overflow-hidden bg-linear-to-br from-primary/90 via-primary to-primary/80 p-12 text-primary-foreground lg:flex">
          <div className="relative z-20">
            <Link
              to="/"
              className="flex w-fit items-center gap-2 rounded-full border border-black/10 bg-white/90 px-3 py-2 text-lg font-semibold text-black shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
            >
              <span className="flex size-8 items-center justify-center rounded-lg bg-black text-white">
                <Rocket className="size-4" aria-hidden />
              </span>
              <span>{t("common.deployherBrand")}</span>
            </Link>
          </div>

          <div className="relative z-20 flex h-[500px] items-end justify-center" aria-hidden>
            <div className="relative" style={{ width: "550px", height: "400px" }}>
              <div
                ref={purpleRef}
                className="absolute bottom-0 transition-all duration-700 ease-in-out"
                style={{
                  left: "70px",
                  width: "180px",
                  height: isTyping || isPasswordHiddenWithValue ? "440px" : "400px",
                  backgroundColor: characterFill,
                  border: `2px solid ${characterBorder}`,
                  borderRadius: "10px 10px 0 0",
                  zIndex: 1,
                  transform: isPasswordVisibleWithValue
                    ? "skewX(0deg)"
                    : isTyping || isPasswordHiddenWithValue
                      ? `skewX(${purplePos.bodySkew - 12}deg) translateX(40px)`
                      : `skewX(${purplePos.bodySkew}deg)`,
                  transformOrigin: "bottom center"
                }}
              >
                <div
                  className="absolute flex gap-8 transition-all duration-700 ease-in-out"
                  style={{
                    left: isPasswordVisibleWithValue
                      ? "20px"
                      : isLookingAtEachOther
                        ? "55px"
                        : `${45 + purplePos.faceX}px`,
                    top: isPasswordVisibleWithValue
                      ? "35px"
                      : isLookingAtEachOther
                        ? "65px"
                        : `${40 + purplePos.faceY}px`
                  }}
                >
                  <EyeBall
                    size={18}
                    pupilSize={7}
                    maxDistance={5}
                    eyeColor={characterFeature}
                    pupilColor={characterFeatureDark}
                    isBlinking={isPurpleBlinking}
                    forceLookX={isPasswordVisibleWithValue ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                  />
                  <EyeBall
                    size={18}
                    pupilSize={7}
                    maxDistance={5}
                    eyeColor={characterFeature}
                    pupilColor={characterFeatureDark}
                    isBlinking={isPurpleBlinking}
                    forceLookX={isPasswordVisibleWithValue ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                  />
                </div>
              </div>

              <div
                ref={blackRef}
                className="absolute bottom-0 transition-all duration-700 ease-in-out"
                style={{
                  left: "240px",
                  width: "120px",
                  height: "310px",
                  backgroundColor: characterFill,
                  border: `2px solid ${characterBorder}`,
                  borderRadius: "8px 8px 0 0",
                  zIndex: 2,
                  transform: isPasswordVisibleWithValue
                    ? "skewX(0deg)"
                    : isLookingAtEachOther
                      ? `skewX(${blackPos.bodySkew * 1.5 + 10}deg) translateX(20px)`
                      : isTyping || isPasswordHiddenWithValue
                        ? `skewX(${blackPos.bodySkew * 1.5}deg)`
                        : `skewX(${blackPos.bodySkew}deg)`,
                  transformOrigin: "bottom center"
                }}
              >
                <div
                  className="absolute flex gap-6 transition-all duration-700 ease-in-out"
                  style={{
                    left: isPasswordVisibleWithValue
                      ? "10px"
                      : isLookingAtEachOther
                        ? "32px"
                        : `${26 + blackPos.faceX}px`,
                    top: isPasswordVisibleWithValue
                      ? "28px"
                      : isLookingAtEachOther
                        ? "12px"
                        : `${32 + blackPos.faceY}px`
                  }}
                >
                  <EyeBall
                    size={16}
                    pupilSize={6}
                    maxDistance={4}
                    eyeColor={characterFeature}
                    pupilColor={characterFeatureDark}
                    isBlinking={isBlackBlinking}
                    forceLookX={isPasswordVisibleWithValue ? -4 : isLookingAtEachOther ? 0 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : isLookingAtEachOther ? -4 : undefined}
                  />
                  <EyeBall
                    size={16}
                    pupilSize={6}
                    maxDistance={4}
                    eyeColor={characterFeature}
                    pupilColor={characterFeatureDark}
                    isBlinking={isBlackBlinking}
                    forceLookX={isPasswordVisibleWithValue ? -4 : isLookingAtEachOther ? 0 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : isLookingAtEachOther ? -4 : undefined}
                  />
                </div>
              </div>

              <div
                ref={orangeRef}
                className="absolute bottom-0 transition-all duration-700 ease-in-out"
                style={{
                  left: "0px",
                  width: "240px",
                  height: "200px",
                  zIndex: 3,
                  backgroundColor: characterFill,
                  border: `2px solid ${characterBorder}`,
                  borderRadius: "120px 120px 0 0",
                  transform: isPasswordVisibleWithValue ? "skewX(0deg)" : `skewX(${orangePos.bodySkew}deg)`,
                  transformOrigin: "bottom center"
                }}
              >
                <div
                  className="absolute flex gap-8 transition-all duration-200 ease-out"
                  style={{
                    left: isPasswordVisibleWithValue ? "50px" : `${82 + orangePos.faceX}px`,
                    top: isPasswordVisibleWithValue ? "85px" : `${90 + orangePos.faceY}px`
                  }}
                >
                  <Pupil
                    size={12}
                    maxDistance={5}
                    pupilColor={characterFeature}
                    forceLookX={isPasswordVisibleWithValue ? -5 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : undefined}
                  />
                  <Pupil
                    size={12}
                    maxDistance={5}
                    pupilColor={characterFeature}
                    forceLookX={isPasswordVisibleWithValue ? -5 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : undefined}
                  />
                </div>
              </div>

              <div
                ref={yellowRef}
                className="absolute bottom-0 transition-all duration-700 ease-in-out"
                style={{
                  left: "310px",
                  width: "140px",
                  height: "230px",
                  backgroundColor: characterFill,
                  border: `2px solid ${characterBorder}`,
                  borderRadius: "70px 70px 0 0",
                  zIndex: 4,
                  transform: isPasswordVisibleWithValue ? "skewX(0deg)" : `skewX(${yellowPos.bodySkew}deg)`,
                  transformOrigin: "bottom center"
                }}
              >
                <div
                  className="absolute flex gap-6 transition-all duration-200 ease-out"
                  style={{
                    left: isPasswordVisibleWithValue ? "20px" : `${52 + yellowPos.faceX}px`,
                    top: isPasswordVisibleWithValue ? "35px" : `${40 + yellowPos.faceY}px`
                  }}
                >
                  <Pupil
                    size={12}
                    maxDistance={5}
                    pupilColor={characterFeature}
                    forceLookX={isPasswordVisibleWithValue ? -5 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : undefined}
                  />
                  <Pupil
                    size={12}
                    maxDistance={5}
                    pupilColor={characterFeature}
                    forceLookX={isPasswordVisibleWithValue ? -5 : undefined}
                    forceLookY={isPasswordVisibleWithValue ? -4 : undefined}
                  />
                </div>
                <div
                  className="absolute h-[4px] w-20 rounded-full transition-all duration-200 ease-out"
                  style={{
                    left: isPasswordVisibleWithValue ? "10px" : `${40 + yellowPos.faceX}px`,
                    top: isPasswordVisibleWithValue ? "88px" : `${88 + yellowPos.faceY}px`,
                    backgroundColor: characterFeature
                  }}
                />
              </div>
            </div>
          </div>

          <div className="relative z-20 flex items-center gap-8 text-sm text-primary-foreground/60">
            <Link
              to="/"
              className="transition-colors hover:text-primary-foreground"
            >
              Privacy Policy
            </Link>
            <Link to="/" className="transition-colors hover:text-primary-foreground">
              Terms of Service
            </Link>
            <Link to="/" className="transition-colors hover:text-primary-foreground">
              Contact
            </Link>
          </div>

          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-size-[20px_20px]" />
          <div className="absolute right-1/4 top-1/4 size-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 size-96 rounded-full bg-white/5 blur-3xl" />
        </div>

        <div className="flex items-center justify-center bg-background p-8">
          <div className="w-full max-w-[420px]">
            <div className="mb-12 flex items-center justify-center gap-2 text-lg font-semibold lg:hidden">
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">
                <Rocket className="size-4 text-primary" aria-hidden />
              </span>
              <span>{t("common.deployherBrand")}</span>
            </div>

            <div className="mb-10 text-center">
              <h1 className="mb-2 text-3xl font-bold tracking-tight">
                {modeTitle}
              </h1>
              <p className="text-sm text-muted-foreground">{modeSubtitle}</p>
            </div>

            <form id="auth-form" onSubmit={handleEmailSubmit} className="space-y-5">
              {mode === "signup" ? (
                <div className="flex items-center gap-4 rounded-lg border border-border/60 p-3">
                  <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="" className="size-full object-cover" />
                    ) : (
                      <Camera className="size-6 text-muted-foreground" aria-hidden />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{t("login.profilePhoto")}</p>
                    <p className="text-xs text-muted-foreground">{t("login.profilePhotoHint")}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()}>
                        {t("login.choosePhoto")}
                      </Button>
                      {avatarFile ? (
                        <Button type="button" variant="ghost" size="icon" onClick={() => setAvatarFile(null)} aria-label={t("login.removePhoto")}>
                          <Trash2 className="size-4" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(event) => setAvatarFile(event.target.files?.[0] ?? null)}
                    />
                  </div>
                </div>
              ) : null}

              {mode === "signup" ? (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    {t("login.name")}
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Anna"
                    value={name}
                    autoComplete="name"
                    onChange={(event) => setName(event.target.value)}
                    required
                    className="h-12 border-border/60 bg-background focus-visible:border-primary"
                  />
                </div>
              ) : null}

              {mode !== "reset" ? <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  {t("login.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="anna@gmail.com"
                  value={email}
                  autoComplete={mode === "signin" ? "username webauthn" : "email"}
                  onChange={(event) => setEmail(event.target.value)}
                  onFocus={() => setIsTyping(true)}
                  onBlur={() => setIsTyping(false)}
                  required
                  className="h-12 border-border/60 bg-background focus-visible:border-primary"
                />
              </div> : null}

              {mode !== "forgot" ? <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  {mode === "reset" ? t("login.newPassword") : t("login.password")}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    maxLength={128}
                    autoComplete={mode === "signin" ? "current-password webauthn" : "new-password"}
                    onChange={(event) => setPassword(event.target.value)}
                    onFocus={() => setIsTyping(true)}
                    onBlur={() => setIsTyping(false)}
                    required
                    className="h-12 border-border/60 bg-background pr-10 focus-visible:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  >
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
              </div> : null}

              {mode === "reset" ? (
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-medium">{t("login.confirmPassword")}</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    maxLength={128}
                    autoComplete="new-password"
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    required
                    className="h-12 border-border/60 bg-background focus-visible:border-primary"
                  />
                </div>
              ) : null}

              {mode === "signin" ? <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox id="remember" checked={rememberMe} onCheckedChange={(checked) => setRememberMe(checked === true)} />
                  <Label htmlFor="remember" className="cursor-pointer text-sm font-normal">
                    {t("login.remember30Days")}
                  </Label>
                </div>
                <button type="button" onClick={() => { setMode("forgot"); setFormError(""); setFormMessage(""); }} className="text-sm font-medium text-primary hover:underline">
                  {t("login.forgotPassword")}
                </button>
              </div> : null}

              {oauthMessage ? (
                <p
                  role="alert"
                  className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {oauthMessage}
                </p>
              ) : null}

              {formError ? (
                <p role="alert" className="rounded-lg border border-red-900/30 bg-red-950/20 p-3 text-sm text-red-400">
                  {formError}
                </p>
              ) : null}

              {formMessage ? (
                <p role="status" className="rounded-lg border border-emerald-700/30 bg-emerald-950/20 p-3 text-sm text-emerald-400">
                  {formMessage}
                </p>
              ) : null}

              {showLoggedInOauthRecovery ? (
                <Button asChild size="lg" className="h-12 w-full text-base font-medium">
                  <a id="continue-dashboard" href={callbackURL}>
                    {t("login.continueDashboard")}
                  </a>
                </Button>
              ) : (
                <Button type="submit" className="h-12 w-full text-base font-medium" size="lg" disabled={isLoading}>
                  {submitLabel}
                </Button>
              )}
            </form>

            {!showLoggedInOauthRecovery && (mode === "signin" || mode === "signup") ? (
              <div className="mt-6 space-y-3">
                {mode === "signin" ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full border-border/60 bg-background text-base font-medium hover:bg-accent"
                    onClick={() => void handlePasskeySignIn()}
                    disabled={isLoading}
                  >
                    {t("login.signInPasskey")}
                  </Button>
                ) : null}
                <Button
                  id="sign-in"
                  type="button"
                  variant="outline"
                  className="h-12 w-full border-border/60 bg-background text-base font-medium hover:bg-accent"
                  onClick={handleSignInClick}
                  aria-label={t("login.signInGithubAria")}
                >
                  <GitHubMark className="mr-2 size-5" />
                  {t("login.signInGithub")}
                </Button>
              </div>
            ) : null}

            <div className="mt-8 text-center text-sm text-muted-foreground">
              {mode === "forgot" || mode === "reset" ? null : mode === "signup" ? t("login.alreadyAccount") : t("login.noAccount")}{" "}
              <button
                type="button"
                onClick={() => {
                  setMode((current) => current === "signin" ? "signup" : "signin");
                  setFormError("");
                  setFormMessage("");
                }}
                className="font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {mode === "forgot" || mode === "reset" ? t("login.backToSignIn") : mode === "signup" ? t("login.logIn") : t("login.createAccount")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LoginRouteInner = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionUser, setSessionUser] = useState(false);

  const resetCapture = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      mode: p.get("mode"),
      token: p.get("token"),
      error: p.get("mode") === "reset" ? p.get("error") : null
    };
  }, []);

  const oauthCapture = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("mode") === "reset"
      ? { error: null, errorDescription: null }
      : { error: p.get("error"), errorDescription: p.get("error_description") };
  }, []);

  useEffect(() => {
    void fetch(resolveApiUrl("/api/session"), { credentials: "include" })
      .then((r) => r.json() as Promise<{ user: unknown }>)
      .then((j) => setSessionUser(j.user != null))
      .catch(() => setSessionUser(false));
  }, []);

  useEffect(() => {
    if (!searchParams.has("error") && !searchParams.has("error_description")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    next.delete("error_description");
    navigate({ search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
  }, [navigate, searchParams]);

  const redirect = resolveSafeRedirect(searchParams.get("redirect"), "/dashboard");
  const callbackURL = `${window.location.origin}${redirect}`;

  const oauth = {
    error: oauthCapture.error,
    errorDescription: oauthCapture.errorDescription,
    loggedIn: sessionUser
  };

  const initialMode: AuthMode =
    resetCapture.mode === "reset" ? "reset" : resetCapture.mode === "forgot" ? "forgot" : "signin";

  return (
    <LoginPage
      callbackURL={callbackURL}
      oauth={oauth}
      initialMode={initialMode}
      resetToken={resetCapture.token}
      resetError={resetCapture.error}
    />
  );
};
